/* Copyright (c) 2020-present CloudIO Inc */

import {
  atom,
  useLatest,
  useStateXAtom,
  useStateXAtomValue,
} from '@cloudio-saas/statex';
import type { VirtualElement } from '@popperjs/core';
import {
  IconButton,
  Theme,
  Paper,
  Popper,
  PopperPlacementType,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import zIndex from 'constants/ZIndex';
import useDebounce from 'hooks/useDebounce';
import Icon from 'icons/Icon';
import React, { useCallback, useEffect, useRef } from 'react';
import type { Instance } from '@popperjs/core';
import { tooltipsDelayAtom } from 'utils/state';
import useUser from 'root/useUser';
import Markdown from 'layout/Sidebar/panels/ComponentsView/Markdown';
import { draggingAtom } from 'designer/atoms';
import useEventListener from './useEventListener';
import { testRunningAtom } from '../layout/Sidebar/panels/TestsView/state';

export const tipBGColor = 'var(--tooltipBackgroundColor)';
export const tipColor = 'var(--tooltipColor)';

const useStyles = makeStyles((theme: Theme) => {
  return {
    error: {},
    paper: ({ width, error }: { width: number; error: boolean }) => ({
      maxWidth: width,
      overflow: 'auto',
      padding: '4px 8px',
      backgroundColor: error ? theme.palette.error.dark : tipBGColor,
      color: error ? '#fff' : tipColor,
      borderRadius: '4px',
      userSelect: 'text',
    }),
    text: ({ error }: { error: boolean }) => ({
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap',
      backgroundColor: error ? theme.palette.error.dark : tipBGColor,
      color: error ? '#fff' : tipColor,
    }),
    innerSpan: ({ width }: { width: number }) => ({
      maxWidth: width,
      fontSize: '1em',
      whiteSpace: 'pre-wrap',
      userSelect: 'text',
    }),
    popper: {
      padding: '0',
      zIndex: zIndex.Tooltip,
      '&[data-popper-placement^="bottom"] > $arrow': {
        top: 0,
        left: 0,
        marginTop: '-6px',
        width: '3em',
        height: '1.1em',
        '&::before': {
          borderWidth: '0 1em 1em 1em',
          borderColor: `transparent transparent ${tipBGColor} transparent`,
        },
        '&$error': {
          '&::before': {
            borderColor: `transparent transparent ${theme.palette.error.dark} transparent`,
          },
        },
      },
      '&[data-popper-placement^="top"] > $arrow': {
        bottom: 0,
        left: 0,
        marginBottom: '-1em',
        width: '3em',
        height: '1.1em',
        '&::before': {
          borderWidth: '1em 1em 0 1em',
          borderColor: `${tipBGColor} transparent transparent transparent`,
        },
        '&$error': {
          '&::before': {
            borderColor: `${theme.palette.error.dark} transparent transparent transparent`,
          },
        },
      },
      '&[data-popper-placement^="right"] > $arrow': {
        left: 0,
        top: 0,
        marginLeft: '-1em',
        height: '3em',
        width: '1.1em',
        '&::before': {
          borderWidth: '1em 1em 1em 0',
          borderColor: `transparent ${tipBGColor} transparent transparent`,
        },
        '&$error': {
          '&::before': {
            borderColor: `transparent ${theme.palette.error.dark} transparent transparent`,
          },
        },
      },
      '&[data-popper-placement^="left"] > $arrow': {
        right: 0,
        top: 0,
        marginRight: '-1em',
        height: '3em',
        width: '1.1em',
        '&::before': {
          borderWidth: '1em 0 1em 1em',
          borderColor: `transparent transparent transparent ${tipBGColor}`,
        },
        '&$error': {
          '&::before': {
            borderColor: `transparent transparent transparent ${theme.palette.error.dark}`,
          },
        },
      },
    },
    popperText: {
      '& $paper': ({ error }: { error: boolean }) => ({
        color: error ? '#fff' : tipColor,
        backgroundColor: error ? theme.palette.error.dark : tipBGColor,
      }),
      '&[data-popper-placement^="bottom"] > $arrow': {
        '&::before': {
          borderColor: `transparent transparent ${tipBGColor} transparent`,
        },
        '&$error': {
          '&::before': {
            borderColor: `transparent transparent ${theme.palette.error.dark} transparent`,
          },
        },
      },
      '&[data-popper-placement^="top"] > $arrow': {
        '&::before': {
          borderColor: `${tipBGColor} transparent transparent transparent`,
        },
        '&$error': {
          '&::before': {
            borderColor: `${theme.palette.error.dark} transparent transparent transparent`,
          },
        },
      },
      '&[data-popper-placement^="right"] > $arrow': {
        '&::before': {
          borderColor: `transparent ${tipBGColor} transparent transparent`,
        },
        '&$error': {
          '&::before': {
            borderColor: `transparent ${theme.palette.error.dark} transparent transparent`,
          },
        },
      },
      '&[data-popper-placement^="left"] > $arrow': {
        '&::before': {
          borderColor: `transparent transparent transparent ${tipBGColor}`,
        },
        '&$error': {
          '&::before': {
            borderColor: `transparent transparent transparent ${theme.palette.error.dark}`,
          },
        },
      },
    },
    arrow: {
      position: 'absolute',
      fontSize: 7,
      zIndex: zIndex.Tooltip + 1,
      width: '3em',
      height: '3em',
      '&::before': {
        content: '""',
        margin: 'auto',
        display: 'block',
        width: 0,
        height: 0,
        borderStyle: 'solid',
      },
    },
    close: {
      position: 'absolute',
      top: 0,
      right: 0,
      cursor: 'pointer',
    },
    clickActive: {},
  };
});

export const TooltipInner = ({
  at,
  clickActive,
  closeTimeoutRef,
  closeTip,
  ele,
  error = false,
  html = false,
  markdown = false,
  maxHeight = 400,
  popperRef,
  tip,
  width = 600,
}: {
  at: PopperPlacementType;
  clickActive?: boolean;
  closeTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  closeTip: () => void;
  ele: VirtualElement | null;
  error?: boolean;
  html?: boolean;
  markdown?: boolean;
  maxHeight?: number;
  popperRef?: React.RefObject<Instance>;
  tip: string | null;
  width?: number;
}) => {
  const classes = useStyles({ width, error });

  if (ele instanceof HTMLElement) {
    const box = ele.getBoundingClientRect();
    if (box.top === 0 && box.left === 0) {
      return null;
    }
  }

  if (!ele) {
    return null;
  }

  return (
    <Popper
      key="popper"
      popperRef={popperRef}
      className={clsx('tooltip', {
        [classes.popper]: true,
        [classes.popperText]: !(html || markdown),
      })}
      placement={at}
      disablePortal={false}
      open={!!ele}
      keepMounted
      anchorEl={ele}
      onMouseEnter={() => {
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
      }}
      onMouseLeave={() => {
        if (clickActive) {
          return;
        }
        closeTimeoutRef.current = setTimeout(() => {
          closeTip();
        }, 300);
      }}
      modifiers={[
        {
          name: 'flip',
          enabled: true,
          options: {
            altBoundary: true,
            boundary: 'viewport',
            rootBoundary: 'viewport',
            padding: 8,
          },
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            altAxis: true,
            mainAxis: true,
            altBoundary: true,
            tether: true,
            boundary: 'viewport',
            rootBoundary: 'viewport',
            padding: 8,
          },
        },
        {
          name: 'arrow',
          enabled: true,
          options: {
            padding: 8,
            // element: arrowRef.current,
          },
        },
        {
          name: 'offset',
          enabled: true,
          options: { offset: [0, 10] },
        },
      ]}
    >
      <div
        data-popper-arrow
        className={clsx(classes.arrow, error ? classes.error : null)}
      />
      <Paper
        key="paper"
        className={clsx('tooltip-body', {
          [classes.paper]: true,
          [classes.text]: !(html || markdown),
          [classes.error]: error,
          [classes.clickActive]: clickActive,
        })}
        style={{ maxHeight }}
      >
        {clickActive ? (
          <IconButton onClick={closeTip} className={classes.close}>
            <span aria-hidden="false" style={{ display: 'none' }}>
              Close
            </span>
            <Icon iconName="times" iconButton />
          </IconButton>
        ) : null}
        {html ? (
          <span
            className={classes.innerSpan}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: tip || '' }}
          />
        ) : markdown && tip ? (
          <Markdown>{tip}</Markdown>
        ) : (
          <div className={classes.innerSpan}>{tip}</div>
        )}
      </Paper>
    </Popper>
  );
};

interface State {
  el: Element | null;
  tip: string | null;
  at: PopperPlacementType;
  html: boolean;
  markdown: boolean;
  error: boolean;
  width: number;
}

const EMPTY: State = {
  el: null,
  tip: null,
  at: 'top',
  html: false,
  markdown: false,
  error: false,
  width: 600,
};

const stateAtom = atom<State>({
  path: ['tooltip', 'state'],
  defaultValue: EMPTY,
});

const clickActiveAtom = atom({
  path: ['tooltip', 'clickActive'],
  defaultValue: false,
});

const delayAtom = atom({
  path: ['tooltip', 'delay'],
  defaultValue: '1000',
});

const Tooltip = React.memo(() => {
  const testRunning = useStateXAtomValue(testRunningAtom);
  const [{ el, tip, at, html, markdown, error, width }, setTip] =
    useStateXAtom<State>(stateAtom);
  const elRef = useLatest(el);
  const popperRef = useRef<Instance>(null);
  const [clickActive, setClickActive] = useStateXAtom(clickActiveAtom);
  const user = useUser();
  const delayConfig = useStateXAtomValue(tooltipsDelayAtom, {
    params: { userName: user.userName },
  });
  const [delay, setDelay] = useStateXAtom(delayAtom);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingEl = useRef<Element | null>(null);

  useEffect(() => {
    setDelay(`${delayConfig}`);
  }, [delayConfig, setDelay]);

  const [debounceSetTip, cancelDebounce, setTipImmediately] = useDebounce(
    (state: State) => {
      pendingEl.current = null;
      setTip(state);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setDelay('0');
    },
  );

  const onMouseEnter = (e: MouseEvent) => {
    if (clickActive) {
      return;
    }
    const target: HTMLElement | null = e.target as HTMLElement;
    if (!(target instanceof Element && document.body.contains(target))) {
      return;
    }
    if (popperRef.current?.state.elements.popper.contains(target)) {
      if (closeTimeoutRef.current) {
        // cancel the closeTip to prevent flicker
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      return;
    }
    if (target) {
      const dataTip = target.getAttribute('data-tip');
      const onellipsis = target.getAttribute('data-tip-onellipsis') === 'true';
      const onclick = target.getAttribute('data-tip-onclick') === 'true';
      if (
        dataTip &&
        !onclick &&
        document.body.contains(target) &&
        (!onellipsis || target.offsetWidth < target.scrollWidth)
      ) {
        const dataAt =
          (target.getAttribute('data-tip-at') as PopperPlacementType) || 'top';
        const _html = target.getAttribute('data-tip-html') === 'true';
        const _markdown = target.getAttribute('data-tip-markdown') === 'true';
        const _error = target.getAttribute('data-tip-error') === 'true';
        const _width = parseInt(
          target.getAttribute('data-tip-width') ?? '300',
          10,
        );
        const delayMs =
          delay === '0'
            ? 0
            : parseInt(
                target.getAttribute('data-tip-delay') ??
                  (onellipsis ? '0' : delay),
                10,
              );
        pendingEl.current = target;
        const state = {
          el: target,
          tip: dataTip,
          at: dataAt,
          html: _html,
          markdown: _markdown,
          error: _error,
          width: _width,
        };
        setDelay(`${delayMs}`);
        if (delayMs === 0) {
          setTipImmediately(state);
        } else {
          debounceSetTip(delayMs, state);
        }
      }
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, []);

  const closeTip = useCallback(() => {
    pendingEl.current = null;
    cancelDebounce();
    setTip(EMPTY);
    setClickActive(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const to = setTimeout(() => {
      setDelay(`${delayConfig}`);
      timeoutRef.current = null;
    }, 1000);
    timeoutRef.current = to;
  }, [cancelDebounce, delayConfig, setClickActive, setDelay, setTip]);

  const onClick = (e: MouseEvent) => {
    const { target: _target } = e;
    if (!(_target instanceof Element)) {
      return;
    }
    let target: Element | null = _target;
    if (popperRef.current?.state.elements.popper.contains(target)) {
      return;
    }

    if (elRef.current && !elRef.current.contains(target)) {
      closeTip();
      return;
    }
    if (!elRef.current) {
      cancelDebounce();
    }
    let found = false;
    while (target) {
      const dataTip = target.getAttribute('data-tip');
      const onclick = target.getAttribute('data-tip-onclick');
      if (dataTip && onclick) {
        const dataAt =
          (target.getAttribute('data-tip-at') as PopperPlacementType) || 'top';
        const dataHtml = !!target.getAttribute('data-tip-html');
        const dataMarkdown = !!target.getAttribute('data-tip-markdown');
        setTipImmediately({
          el: target,
          tip: dataTip,
          at: dataAt,
          html: dataHtml,
          markdown: dataMarkdown,
          error: false,
        });
        setClickActive(true);
        found = true;
        break;
      } else {
        target = target.parentElement;
      }
    }
    if (!found) {
      closeTip();
    }
  };

  function onMouseLeave(e: MouseEvent) {
    if (clickActive || !(e.relatedTarget instanceof Node)) {
      return;
    }
    if (e.target === el) {
      if (popperRef.current?.state.elements.popper.contains(e.relatedTarget)) {
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
        return;
      }
      closeTimeoutRef.current = setTimeout(() => {
        closeTip();
      }, 300);
    } else if (pendingEl.current === e.target) {
      cancelDebounce();
    }
  }

  function onVisibilityChange() {
    if (clickActive) {
      return;
    }
    closeTip();
  }

  useEventListener('mouseenter', onMouseEnter, document, true);
  useEventListener('mouseleave', onMouseLeave, document, true);
  useEventListener('click', onClick, document, true);
  useEventListener('visibilitychange', onVisibilityChange, document, false);
  useEventListener('blur', onVisibilityChange, document, false);
  let maxHeight = 400;

  const dragging = useStateXAtomValue(draggingAtom);

  if (dragging || testRunning) {
    // don't show tooltip when dragging or running tests
    return null;
  }

  let ele = el;
  if (el) {
    if (!document.body.contains(el)) {
      // element is removed from dom
      ele = null;
    }
    const box = el.getBoundingClientRect();
    if (box.width === 0 && box.height === 0) {
      // element is not visible
      ele = null;
    }
    const bottom = window.innerHeight - box.y;
    maxHeight = (bottom > box.y ? bottom : box.y) - 40;
  } else {
    ele = null;
  }

  return (
    <TooltipInner
      at={at}
      clickActive={clickActive}
      closeTimeoutRef={closeTimeoutRef}
      closeTip={closeTip}
      ele={ele}
      error={error}
      html={html}
      markdown={markdown}
      maxHeight={maxHeight}
      popperRef={popperRef}
      tip={tip}
      width={width}
    />
  );
});

Tooltip.displayName = 'Tooltip';

export default Tooltip;
